// ======================================================================
// File: src/modules/ipe/dashboard/crm/components/PendingQuotationsWidget.jsx
// Purpose:
// CRM Executive Quotation Workbench
// ======================================================================

import React from "react";

export default function PendingQuotationsWidget({
  quotations = [],
  onViewAll,
  onRowClick,
}) {
  const getStatusStyle = (status) => {
    switch (status) {
      case "Pending Approval":
        return {
          bg: "#FEF3C7",
          color: "#B45309",
        };

      case "Negotiation":
        return {
          bg: "#DBEAFE",
          color: "#1D4ED8",
        };

      case "Approved":
        return {
          bg: "#DCFCE7",
          color: "#15803D",
        };

      default:
        return {
          bg: "#E5E7EB",
          color: "#374151",
        };
    }
  };

  const getNumericValue = (value) => {
    if (!value) return 0;

    const number = parseFloat(
      value.replace(/[^\d.]/g, "")
    );

    return isNaN(number)
      ? 0
      : number;
  };

  const maxValue =
    Math.max(
      ...quotations.map((q) =>
        getNumericValue(q.value)
      ),
      1
    );

  const totalValue = quotations.reduce(
    (sum, q) =>
      sum + getNumericValue(q.value),
    0
  );

  const pendingCount = quotations.filter(
    (q) =>
      q.status ===
      "Pending Approval"
  ).length;

  const negotiationCount =
    quotations.filter(
      (q) =>
        q.status ===
        "Negotiation"
    ).length;

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "12px",
        padding: "18px",
        boxShadow:
          "0 2px 10px rgba(0,0,0,.06)",
        border: "1px solid #e5e7eb",
      }}
    >
      {/* =====================================================
          HEADER
      ===================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          marginBottom: "18px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: 700,
          }}
        >
          Pending Quotations
        </h3>

        <button
          onClick={onViewAll}
          style={{
            border:
              "1px solid #d1d5db",
            background: "#fff",
            padding: "6px 12px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          View All
        </button>
      </div>

      {/* =====================================================
          SUMMARY
      ===================================================== */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "1fr 1fr 1fr",
          gap: "12px",
          marginBottom: "18px",
        }}
      >
        <div
          style={{
            background: "#eff6ff",
            padding: "12px",
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
            }}
          >
            Total Value
          </div>

          <div
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#2563eb",
            }}
          >
            ₹ {totalValue}
          </div>
        </div>

        <div
          style={{
            background: "#fef3c7",
            padding: "12px",
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
            }}
          >
            Pending Approval
          </div>

          <div
            style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#b45309",
            }}
          >
            {pendingCount}
          </div>
        </div>

        <div
          style={{
            background: "#dbeafe",
            padding: "12px",
            borderRadius: "10px",
          }}
        >
          <div
            style={{
              fontSize: "11px",
              color: "#64748b",
            }}
          >
            Negotiation
          </div>

          <div
            style={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#2563eb",
            }}
          >
            {negotiationCount}
          </div>
        </div>
      </div>

      {/* =====================================================
          QUOTATION LIST
      ===================================================== */}

      <div>
        {quotations.map(
          (row, index) => {
            const status =
              getStatusStyle(
                row.status
              );

            const valueWidth =
              (getNumericValue(
                row.value
              ) /
                maxValue) *
              100;

            return (
              <div
                key={
                  row.quotation_no
                }
                onClick={() =>
                  onRowClick?.(
                    row
                  )
                }
                style={{
                  border:
                    "1px solid #e5e7eb",
                  borderRadius:
                    "10px",
                  padding:
                    "14px",
                  marginBottom:
                    "10px",
                  cursor:
                    "pointer",
                  transition:
                    "all .25s ease",
                }}
                onMouseEnter={(
                  e
                ) => {
                  e.currentTarget.style.transform =
                    "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 18px rgba(0,0,0,.08)";
                }}
                onMouseLeave={(
                  e
                ) => {
                  e.currentTarget.style.transform =
                    "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "none";
                }}
              >
                {/* TOP ROW */}

                <div
                  style={{
                    display:
                      "flex",
                    alignItems:
                      "center",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      width:
                        "42px",
                      height:
                        "42px",
                      borderRadius:
                        "50%",
                      background:
                        "#7c3aed",
                      color:
                        "#fff",
                      display:
                        "flex",
                      alignItems:
                        "center",
                      justifyContent:
                        "center",
                      fontWeight: 700,
                    }}
                  >
                    {row.customer
                      ?.substring(
                        0,
                        1
                      )
                      ?.toUpperCase()}
                  </div>

                  <div
                    style={{
                      flex: 1,
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        color:
                          "#111827",
                      }}
                    >
                      {
                        row.customer
                      }
                    </div>

                    <div
                      style={{
                        fontSize:
                          "12px",
                        color:
                          "#64748b",
                      }}
                    >
                      {
                        row.quotation_no
                      }
                    </div>
                  </div>

                  <span
                    style={{
                      background:
                        status.bg,
                      color:
                        status.color,
                      padding:
                        "4px 10px",
                      borderRadius:
                        "999px",
                      fontSize:
                        "12px",
                      fontWeight: 700,
                    }}
                  >
                    {row.status}
                  </span>
                </div>

                {/* VALUE */}

                <div
                  style={{
                    marginTop:
                      "12px",
                  }}
                >
                  <div
                    style={{
                      display:
                        "flex",
                      justifyContent:
                        "space-between",
                      marginBottom:
                        "6px",
                    }}
                  >
                    <span
                      style={{
                        fontSize:
                          "12px",
                        color:
                          "#64748b",
                      }}
                    >
                      Quotation Value
                    </span>

                    <strong>
                      {row.value}
                    </strong>
                  </div>

                  <div
                    style={{
                      height:
                        "8px",
                      background:
                        "#e5e7eb",
                      borderRadius:
                        "999px",
                      overflow:
                        "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${valueWidth}%`,
                        height:
                          "100%",
                        background:
                          "#7c3aed",
                      }}
                    />
                  </div>
                </div>

                {/* FOOTER */}

                <div
                  style={{
                    marginTop:
                      "10px",
                    display:
                      "flex",
                    justifyContent:
                      "space-between",
                    fontSize:
                      "12px",
                    color:
                      "#64748b",
                  }}
                >
                  <span>
                    Priority:
                    High
                  </span>

                  <span>
                    Ageing:
                    {" "}
                    {index + 2}
                    {" "}
                    Days
                  </span>
                </div>
              </div>
            );
          }
        )}
      </div>
    </div>
  );
}